import { useCallback, useState, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    /** Badge to show condition for conditional fields */
    conditionBadge?: string
    /** Demo animation type */
    demo?: TourDemoType
    /** Action to perform before showing step */
    action?: () => void
}

interface UseListTourProps {
    onCloseCreateModal: () => void
}

/**
 * Tour hook for Analytics List Tab
 */
export const useListTour = ({ onCloseCreateModal }: UseListTourProps) => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)

    const tourSteps: TourStep[] = [
        {
            selector: '[data-tour="target-analytics-list-container"]',
            title: 'Danh sách KPI',
            description:
                'Quản lý KPI của nhân viên (Presentation, POC, Contract, Doanh số)',
            action: () => {
                // Ensure modal is closed when starting
                onCloseCreateModal()
            },
        },
        {
            selector: '[data-tour="target-analytics-create-btn"]',
            title: 'Tạo mới KPI',
            description: 'Tạo KPI mới cho nhân viên hoặc nhóm nhân viên',
        },
    ]

    const showTourUI = (step: TourStep, stepIndex: number) => {
        // Run action if defined (e.g. open modal)
        if (step.action) {
            step.action()
        }

        // Delay to allow UI to update (modal to open/close)
        const delay = step.action ? 500 : 100

        setTimeout(() => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip if element not found
                if (stepIndex < tourSteps.length - 1) {
                    goToStep(stepIndex + 1)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: tourSteps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1)
                            : undefined,
                    onNext: () => {
                        if (stepIndex === tourSteps.length - 1) {
                            endTour()
                        } else {
                            goToStep(stepIndex + 1)
                        }
                    },
                    onClose: endTour,
                    isLastStep: stepIndex === tourSteps.length - 1,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        }, delay)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex)
        setCurrentTourStep(stepIndex)
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
        onCloseCreateModal()
    }, [onCloseCreateModal])

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)
        // Ensure modal is closed initially
        onCloseCreateModal()

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        setTimeout(() => {
            showTourUI(tourSteps[0], 0)
        }, 600)
    }, [onCloseCreateModal])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive, currentStep: currentTourStep }
}

export default useListTour
